import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Phone, User, MapPin, Save, Plus, Trash2, Edit2, ShieldCheck, Heart, ArrowRight, ChevronDown, X } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useWishlist } from '../context/WishlistContext';
import LocationAutofillButton from '../components/LocationAutofillButton';

// Indian States and Union Territories
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

const emptyAddress = {
  house_flat_no: '', // Required: House / Flat Number
  street_area: '', // Required: Street / Area
  building_name: '', // Optional: Building Name
  apartment_suite: '', // Optional: Apartment / Suite
  floor: '', // Optional: Floor
  landmark: '', // Optional: Landmark
  city: '', // Required: City
  state_province: '', // Required: State / Province
  postal_code: '', // Required: Postal Code / ZIP
  country: 'India', // Default to India for domestic
  district_county: '', // Optional: District / County
  latitude: null,
  longitude: null,
};

export default function Profile() {
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser, logout } = useAuth();
  const { openAuth } = useAuthModal();
  const { wishlist, wishlistCount } = useWishlist();

  const [profile, setProfile] = useState({ name: '', email: '', phone: '', mobile: '' });
  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState(emptyAddress);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isInternational, setIsInternational] = useState(false); // Default to Domestic
  const [showAddressModal, setShowAddressModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/"); // Redirect to home without showing popup
      return;
    }
    loadProfile();
  }, [isAuthenticated]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [meRes, addressRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/user/addresses'),
      ]);
      setProfile({
        name: meRes.data.name || '',
        email: meRes.data.email || '',
        phone: meRes.data.phone || '',
        mobile: meRes.data.mobile || '',
      });
      setAddresses(addressRes.data.addresses || []);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      const payload = {
        name: profile.name,
        email: profile.email,
      };
      
      // Only include phone if it's set
      if (profile.phone) {
        payload.phone = profile.phone;
      }
      
      // Always include mobile if it's set and not a temporary Google mobile
      if (profile.mobile && !profile.mobile.startsWith('g_')) {
        payload.mobile = profile.mobile;
      }
      
      await api.put('/users/me', payload);
      await refreshUser();
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Profile save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async () => {
    // Required fields validation
    if (!address.house_flat_no?.trim() || !address.street_area?.trim() || 
        !address.city?.trim() || !address.state_province?.trim() || 
        !address.postal_code?.trim() || !address.country?.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    // Domestic (India) validation for postal code (6 digits)
    if (!isInternational && !/^[0-9]{6}$/.test(address.postal_code)) {
      toast.error('Enter a valid 6-digit pincode for India');
      return;
    }

    // International validation for postal code (minimum 3 characters)
    if (isInternational && address.postal_code.length < 3) {
      toast.error('Enter a valid postal code (minimum 3 characters)');
      return;
    }

    // For domestic, ensure country is India
    if (!isInternational && address.country?.toLowerCase() !== 'india') {
      toast.error('Country must be India for domestic shipping');
      return;
    }

    try {
      setSaving(true);
      const res = editingId
        ? await api.put(`/user/addresses/${editingId}`, address)
        : await api.post('/user/addresses', address);
      setAddresses(res.data.addresses || []);
      setAddress({ ...emptyAddress });
      setEditingId(null);
      setIsInternational(false); // Reset to domestic after save
      setShowAddressModal(false); // Close modal
      toast.success(editingId ? 'Address updated' : 'Address added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Address save failed');
    } finally {
      setSaving(false);
    }
  };

  const editAddress = (item) => {
    setEditingId(item.id);
    const country = item.country || '';
    
    // Set international status based on country
    if (country && country.toLowerCase() !== 'india') {
      setIsInternational(true);
    } else {
      setIsInternational(false);
    }

    setAddress({
      // New field names with fallback to old field names for backward compatibility
      house_flat_no: item.house_flat_no || item.flat_no || item.building_no || '',
      street_area: item.street_area || item.address_line1 || item.street || '',
      building_name: item.building_name || '',
      apartment_suite: item.apartment_suite || item.apt_no || '',
      floor: item.floor || '',
      landmark: item.landmark || item.address_line2 || '',
      city: item.city || '',
      state_province: item.state_province || item.state || '',
      postal_code: item.postal_code || item.pincode || '',
      country: country,
      district_county: item.district_county || item.district || '',
      latitude: item.latitude ?? null,
      longitude: item.longitude ?? null,
    });
    setShowAddressModal(true); // Open modal
  };

  const openAddAddressModal = () => {
    setEditingId(null);
    setAddress({ ...emptyAddress });
    setIsInternational(false);
    setShowAddressModal(true);
  };

  const closeAddressModal = () => {
    setShowAddressModal(false);
    setEditingId(null);
    setAddress({ ...emptyAddress });
    setIsInternational(false);
  };

  const applyLocationAutofill = (fields) => {
    setAddress((prev) => {
      const detectedCountry = fields.country || '';
      
      // Always fill all available fields from location detection (as requested)
      const updatedAddress = {
        ...prev,
        // Map OSM fields to form fields
        house_flat_no: fields.house_flat_no || prev.house_flat_no || '',
        street_area: fields.street_area || prev.street_area || '',
        country: detectedCountry || (isInternational ? '' : 'India'),
        state_province: fields.state_province || '',
        district_county: fields.district_county || '',
        city: fields.city || '',
        postal_code: fields.postal_code || '',
        latitude: fields.latitude ?? null,
        longitude: fields.longitude ?? null,
      };

      // Auto-detect if international based on detected country
      if (detectedCountry && detectedCountry.toLowerCase() !== 'india') {
        setIsInternational(true);
      } else if (detectedCountry && detectedCountry.toLowerCase() === 'india') {
        setIsInternational(false);
      }

      return updatedAddress;
    });
  };

  const deleteAddress = async (id) => {
    try {
      const res = await api.delete(`/user/addresses/${id}`);
      setAddresses(res.data.addresses || []);
      toast.success('Address deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030804] pt-28 px-4 text-emerald-300 font-[Cormorant_Garamond]">
        <div className="max-w-6xl mx-auto">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#030804] via-[#07150d] to-[#030804] pt-16 pb-16 px-4 font-[Cormorant_Garamond] text-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 rounded-xl border border-[#1a4d2e]/50 bg-[#07150d]/80 p-5 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#ffcc32] via-[#ff7a18] to-[#ff2b20] flex items-center justify-center text-3xl font-black text-black shadow-xl shadow-[#ffcc32]/20">
                {(user?.name || user?.mobile || 'R').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-emerald-300 text-sm uppercase tracking-wide">Rider Profile</p>
                <h1 className="text-3xl sm:text-4xl font-black uppercase" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
                  {user?.name || 'Your Account'}
                </h1>
                <p className="text-zinc-400">{user?.mobile?.startsWith('g_') ? 'Mobile number required' : user?.mobile}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="px-5 py-2.5 rounded-lg border border-orange-500/50 text-orange-300 hover:bg-orange-500 hover:text-black font-bold transition"
            >
              Logout
            </button>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="lg:col-span-2 rounded-xl border border-[#1a4d2e]/50 bg-[#0a1a0f]/90 p-5 sm:p-6"
          >
            <h2 className="text-3xl font-black mb-5 flex items-center gap-2 uppercase" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
              <User size={20} className="text-orange-500" /> Contact Details
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="space-y-2">
                <span className="text-sm text-zinc-400">Full name</span>
                <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="w-full bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-4 py-3 outline-none focus:border-emerald-500" />
              </label>
              <label className="space-y-2">
                <span className="text-sm text-zinc-400">Mobile Number *</span>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-3.5 text-zinc-500" />
                  <input value={profile.mobile?.startsWith('g_') ? '' : profile.mobile} onChange={(e) => setProfile({ ...profile, mobile: e.target.value })} className="w-full bg-[#050505] border border-white/15 rounded-lg pl-10 pr-4 py-3 outline-none focus:border-[#ffcc32]" />
                </div>
              </label>
              <label className="space-y-2 sm:col-span-2">
                <span className="text-sm text-zinc-400">Alternate phone</span>
                <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="w-full bg-[#050505] border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-[#ffcc32]" />
              </label>
              <label className="space-y-2 sm:col-span-2">
                <span className="text-sm text-zinc-400">Email</span>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-3.5 text-zinc-500" />
                  <input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="w-full bg-[#050505] border border-white/15 rounded-lg pl-10 pr-4 py-3 outline-none focus:border-[#ffcc32]" />
                </div>
              </label>
            </div>
            <button onClick={saveProfile} disabled={saving} className="mt-5 px-5 py-3 bg-orange-500 hover:bg-orange-400 text-black rounded-lg font-bold flex items-center gap-2 transition">
              <Save size={18} /> Save Profile
            </button>
          </motion.section>

          <motion.aside
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl border border-orange-500/30 bg-gradient-to-br from-[#0a1a0f] to-[#050f0a] p-5 sm:p-6 h-fit"
          >
            <ShieldCheck className="text-emerald-400 mb-3" size={28} />
            <h3 className="text-xl font-bold">Premium Rider Access</h3>
            <p className="text-zinc-400 mt-2 text-sm leading-relaxed">
              Your profile is used for faster checkout, saved delivery addresses, order tracking, and invoice details.
            </p>
          </motion.aside>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-6 rounded-xl border border-[#1a4d2e]/50 bg-[#0a1a0f]/90 p-5 sm:p-6"
        >
          <div className="flex items-center justify-between gap-3 mb-5">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <MapPin size={20} className="text-orange-500" /> Saved Addresses
            </h2>
            <span className="text-xs text-zinc-500">{addresses.length}/3 saved</span>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-5">
            {addresses.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#1a4d2e]/50 bg-[#050f0a]/80 p-4">
                <p className="font-semibold text-white">
                  {item.building_name && `${item.building_name}, `}
                  {item.house_flat_no && `${item.house_flat_no}, `}
                  {item.apartment_suite && `Apt ${item.apartment_suite}, `}
                  {item.floor && `Floor ${item.floor}, `}
                  {item.street_area || item.address_line1 || item.street}
                </p>
                <p className="text-sm text-zinc-400 mt-1">
                  {item.landmark && `${item.landmark}, `}
                  {item.district_county && `${item.district_county}, `}
                  {item.city}, {item.state_province || item.state}{item.country ? `, ${item.country}` : ''} - {item.postal_code || item.pincode}
                </p>
                <div className="flex gap-3 mt-4">
                  <button onClick={() => editAddress(item)} className="text-emerald-300 hover:text-emerald-200 flex items-center gap-1 text-sm">
                    <Edit2 size={14} /> Edit
                  </button>
                  <button onClick={() => deleteAddress(item.id)} className="text-orange-300 hover:text-orange-200 flex items-center gap-1 text-sm">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={openAddAddressModal} 
            disabled={addresses.length >= 3}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition"
          >
            <Plus size={18} /> Add New Address
          </button>
        </motion.section>

        {/* Wishlist Section */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 rounded-xl border border-orange-500/30 bg-gradient-to-br from-[#0a1a0f] to-[#050f0a] p-5 sm:p-6 cursor-pointer hover:border-orange-500/60 transition"
          onClick={() => navigate('/wishlist')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Heart className="text-orange-500" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">My Wishlist</h3>
                <p className="text-zinc-400 text-sm">{wishlistCount} items saved</p>
              </div>
            </div>
            <ArrowRight className="text-orange-500" size={24} />
          </div>
        </motion.section>

        {/* Address Modal */}
        <AnimatePresence>
          {showAddressModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeAddressModal}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#0a1a0f] border border-[#1a4d2e]/50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <MapPin size={20} className="text-orange-500" />
                    {editingId ? 'Edit Address' : 'Add New Address'}
                  </h2>
                  <button
                    onClick={closeAddressModal}
                    className="text-zinc-400 hover:text-white transition"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Domestic/International Toggle */}
                <div className="mb-4 flex items-center gap-3">
                  <span className="text-sm text-zinc-400">Shipping Type:</span>
                  <div className="flex items-center gap-2 bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setIsInternational(false);
                        setAddress(prev => ({ ...prev, country: 'India' }));
                      }}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                        !isInternational 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      Domestic (India)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsInternational(true);
                        setAddress(prev => ({ ...prev, country: '' }));
                      }}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                        isInternational 
                          ? 'bg-orange-600 text-white' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      International
                    </button>
                  </div>
                </div>

                {/* Location Autofill */}
                <div className="mb-4 flex items-center gap-3">
                  <LocationAutofillButton
                    onAutofill={applyLocationAutofill}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <p className="text-xs text-zinc-500">
                    Auto-fill from current location
                  </p>
                </div>

                {/* Compact Grid Layout */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Required Fields */}
                  <input
                    value={address.house_flat_no}
                    onChange={(e) => setAddress({ ...address, house_flat_no: e.target.value })}
                    placeholder="House / Flat *"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  <input
                    value={address.street_area}
                    onChange={(e) => setAddress({ ...address, street_area: e.target.value })}
                    placeholder="Street / Area *"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  
                  {/* Optional Fields */}
                  <input
                    value={address.building_name}
                    onChange={(e) => setAddress({ ...address, building_name: e.target.value })}
                    placeholder="Building (optional)"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  <input
                    value={address.apartment_suite}
                    onChange={(e) => setAddress({ ...address, apartment_suite: e.target.value })}
                    placeholder="Apt/Suite (optional)"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  <input
                    value={address.floor}
                    onChange={(e) => setAddress({ ...address, floor: e.target.value })}
                    placeholder="Floor (optional)"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  <input
                    value={address.landmark}
                    onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                    placeholder="Landmark (optional)"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  
                  {/* Location Fields */}
                  <input
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    placeholder="City *"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  {!isInternational ? (
                    <div className="relative">
                      <select
                        value={address.state_province}
                        onChange={(e) => setAddress({ ...address, state_province: e.target.value })}
                        className="w-full bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500 appearance-none cursor-pointer"
                      >
                        <option value="">Select State *</option>
                        {INDIAN_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                    </div>
                  ) : (
                    <input
                      value={address.state_province}
                      onChange={(e) => setAddress({ ...address, state_province: e.target.value })}
                      placeholder="State/Province *"
                      className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    />
                  )}
                  
                  {/* Country & Postal Code */}
                  <input
                    value={address.country}
                    onChange={(e) => {
                      setAddress({ ...address, country: e.target.value });
                      if (e.target.value && e.target.value.toLowerCase() !== 'india') {
                        setIsInternational(true);
                      } else if (e.target.value && e.target.value.toLowerCase() === 'india') {
                        setIsInternational(false);
                      }
                    }}
                    placeholder="Country *"
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    readOnly={!isInternational}
                  />
                  <input
                    value={address.postal_code}
                    onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                    placeholder={isInternational ? 'Postal Code *' : 'Pincode *'}
                    maxLength={isInternational ? 10 : 6}
                    className="bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  
                  {/* District/County */}
                  <input
                    value={address.district_county}
                    onChange={(e) => setAddress({ ...address, district_county: e.target.value })}
                    placeholder={isInternational ? 'County (optional)' : 'District (optional)'}
                    className="col-span-2 bg-[#050f0a] border border-[#1a4d2e]/60 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={closeAddressModal}
                    className="flex-1 px-4 py-2 border border-white/20 rounded-lg text-zinc-300 hover:text-white hover:border-white/40 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveAddress}
                    disabled={saving || (!editingId && addresses.length >= 3)}
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg font-bold transition"
                  >
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Add Address')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
